import {
  STOCK_COUNT_KIND_LABELS,
  type StockCount,
  type StockCountLine,
  type StockCountScan,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  cn,
  DataTable,
  type DataTableColumn,
  Dialog,
  FormField,
  Input,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Minus, Plus, ScanBarcode, Send, Undo2, X } from "lucide-react";
import { type ReactElement, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalRequestPanel } from "../../../../features/approvals/request-panel.js";
import { Section } from "../../../../features/expenses/shared.js";
import {
  applyScan,
  canDecrement,
  countActions,
  type CountLineFilter,
  countedTarget,
  countProgress,
  differenceDirection,
  differenceSummary,
  filterCountLines,
  findLineByBarcode,
  isScannableBarcode,
  normalizeBarcode,
} from "../../../../features/market/count-values.js";
import {
  formatStockQuantity,
  moneyExport,
  quantityExport,
} from "../../../../features/market/market-values.js";
import { useCount, useMarketPermissions, useRefreshMarket } from "../../../../features/market/queries.js";
import {
  CountStatusBadge,
  crumbLink,
  Field,
  LedgerStatus,
  MarketPage,
} from "../../../../features/market/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel } from "../../../../lib/excel.js";
import { formatDateTime } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";
import { useStepUp } from "../../../../lib/step-up.js";

export const Route = createFileRoute("/_app/market/sayimlar/$id")({ component: CountRoute });

/** Tabletin adı okutmaya yazılır (append-only kayıt: hangi cihazdan okutuldu). */
const DEVICE = "Web";
/** Uzun listelerde ekranda en çok bu kadar satır çizilir; arama daraltır (tam sayımda binlerce ürün olabilir). */
const LIST_LIMIT = 200;

/**
 * SAYIM EKRANI — tablet için tam sayfa (Prompt 16 — web, ADR 0016 §13).
 *
 * Sayılıyor: büyük barkod alanı (okuyucu klavye gibi yazar ve Enter gönderir) → okutulan ürünün kartı (sayılan,
 * `+` / `−` büyük düğmeler, elle miktar) → sayılan / sayılmayan listesi → gönder. Gönderilmiş sayımda FARK RAPORU
 * (beklenen, sayılan, fark, değer; noksan / fazla toplamları) ve onay (`market.count.approve`, step-up).
 *
 * Sayılanın kaynağı SUNUCUDUR: her okutma yanıtı satırın yeni sayılanını döner ve ekran onu yazar (aynı sayımı iki
 * tablet birlikte okutabilir). 820 px genişlikte tek sütunda kullanılabilir.
 */
function CountRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const count = useCount(id);
  const title = count.data?.countNo ?? t("market.counts.detailTitle");
  return (
    <MarketPage
      title={title}
      breadcrumb={[crumbLink(t("menu.marketCounts"), "/market/sayimlar"), { label: title }]}
    >
      {() =>
        count.error ? (
          <Alert tone="danger">{errorMessage(count.error)}</Alert>
        ) : !count.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <CountScreen count={count.data} />
        )
      }
    </MarketPage>
  );
}

function CountScreen({ count }: { count: StockCount }): ReactElement {
  const { t } = useTranslation();
  const permissions = useMarketPermissions();
  const actions = countActions(count);
  const progress = countProgress(count.lines);

  return (
    <div className="mx-auto grid w-full max-w-5xl gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{count.countNo}</span>
        <CountStatusBadge status={count.status} />
        {count.approvalPending ? (
          <StatusBadge tone="warning">{t("market.counts.approvalPending")}</StatusBadge>
        ) : null}
        <span className="text-sm text-muted-foreground">
          {STOCK_COUNT_KIND_LABELS[count.kind]} · {count.stationName} ·{" "}
          <span className="font-mono">{count.warehouseCode}</span>
        </span>
      </div>

      <div className="grid gap-1.5">
        <div className="flex items-center justify-between text-sm">
          <span id="count-progress-label">{t("market.counts.progressLabel")}</span>
          <span className="tabular font-medium">
            {t("market.counts.progressValue", { counted: progress.counted, total: progress.total })}
          </span>
        </div>
        <div
          role="progressbar"
          aria-labelledby="count-progress-label"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.counted}
          className="h-2.5 overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full bg-primary transition-[width]" style={{ width: `${progress.percent}%` }} />
        </div>
      </div>

      {actions.scan && permissions.canManageCounts ? (
        <CountingPanel count={count} />
      ) : count.status === "COUNTING" ? (
        <>
          <Alert tone="info">{t("market.counts.readOnlyCounting")}</Alert>
          <LineList count={count} selectedProductId={null} onSelect={() => undefined} />
        </>
      ) : (
        <DifferenceReport count={count} />
      )}
    </div>
  );
}

// ── Okutma paneli ────────────────────────────────────────────────────────────
type ScanFeedback = { tone: "success"; text: string } | { tone: "danger"; text: string } | null;

function CountingPanel({ count }: { count: StockCount }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = useRefreshMarket();
  const inputRef = useRef<HTMLInputElement>(null);
  const barcodeId = useId();
  const [barcode, setBarcode] = useState("");
  const [feedback, setFeedback] = useState<ScanFeedback>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const selected = count.lines.find((line) => line.productId === selectedProductId) ?? null;
  const countKey = queryKeys.market(count.id, "count");

  /** Okutma yanıtı önbelleğe yazılır: satırın sayılanı SUNUCUNUN verdiği değerdir. */
  const writeScan = (scan: StockCountScan) =>
    queryClient.setQueryData<StockCount>(countKey, (current) => {
      if (!current) return current;
      const lines = applyScan(current.lines, scan);
      return { ...current, lines, countedLineCount: countProgress(lines).counted };
    });

  const scan = useMutation({
    mutationFn: async (body: {
      kind: "SCAN" | "MANUAL" | "CORRECTION";
      barcode?: string;
      productId?: string;
      quantity: string;
    }) =>
      (
        await api.POST("/market/counts/{id}/scans", {
          params: { path: { id: count.id } },
          body: { ...body, device: DEVICE } as never,
        })
      ).data!,
    onSuccess: (result) => {
      writeScan(result as StockCountScan);
      setSelectedProductId(result.productId);
      const line = count.lines.find((item) => item.productId === result.productId);
      setFeedback({
        tone: "success",
        text: t("market.counts.scanned", {
          product: line?.productName ?? result.barcode ?? "",
          counted: formatStockQuantity(result.countedQuantity),
        }),
      });
    },
    onError: (caught) => setFeedback({ tone: "danger", text: errorMessage(caught) }),
    onSettled: () => inputRef.current?.focus(),
  });

  const setCounted = useMutation({
    mutationFn: async (input: { lineId: string; countedQuantity: string }) =>
      (
        await api.PUT("/market/counts/{id}/lines/{lineId}", {
          params: { path: { id: count.id, lineId: input.lineId } },
          body: { countedQuantity: input.countedQuantity },
        })
      ).data!,
    onSuccess: (saved) => {
      queryClient.setQueryData(countKey, saved);
      setFeedback({ tone: "success", text: t("market.counts.countedSet") });
    },
    onError: (caught) => setFeedback({ tone: "danger", text: errorMessage(caught) }),
  });

  const cancel = useMutation({
    mutationFn: async () => {
      const fresh = (await api.GET("/market/counts/{id}", { params: { path: { id: count.id } } })).data!;
      return (
        await api.POST("/market/counts/{id}/cancel", {
          params: { path: { id: count.id } },
          body: { version: fresh.version },
        })
      ).data!;
    },
    onSuccess: async () => {
      setCancelling(false);
      await refresh();
      toast({ tone: "success", title: t("market.counts.done.cancel") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const submitScan = () => {
    const code = normalizeBarcode(barcode);
    setBarcode("");
    if (!isScannableBarcode(code)) {
      setFeedback({ tone: "danger", text: t("market.counts.barcodeTooShort") });
      inputRef.current?.focus();
      return;
    }
    // Ana barkodu satırda olan ürün hemen seçilir; sunucu ikincil barkodları da tanır.
    const local = findLineByBarcode(count.lines, code);
    if (local) setSelectedProductId(local.productId);
    scan.mutate({ kind: "SCAN", barcode: code, quantity: "1" });
  };

  return (
    <>
      <Card className="grid gap-4 p-4 sm:p-5">
        <form
          className="grid gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submitScan();
          }}
        >
          <label htmlFor={barcodeId} className="text-base font-semibold">
            {t("market.counts.barcodeLabel")}
          </label>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              id={barcodeId}
              autoFocus
              autoComplete="off"
              inputMode="numeric"
              enterKeyHint="go"
              className="h-14 flex-1 font-mono text-2xl"
              value={barcode}
              aria-describedby={`${barcodeId}-hint`}
              onChange={(event) => setBarcode(event.target.value)}
            />
            <Button type="submit" size="lg" className="h-14 px-6" loading={scan.isPending}>
              <ScanBarcode aria-hidden="true" />
              {t("market.counts.scan")}
            </Button>
          </div>
          <p id={`${barcodeId}-hint`} className="text-sm text-muted-foreground">
            {t("market.counts.barcodeHint")}
          </p>
        </form>
        <div aria-live="polite" role="status" data-testid="scan-feedback">
          {feedback ? (
            <p
              className={cn(
                "rounded-md px-3 py-2 text-base font-medium",
                feedback.tone === "success"
                  ? "bg-success-subtle text-success"
                  : "bg-danger-subtle text-destructive",
              )}
            >
              {feedback.text}
            </p>
          ) : null}
        </div>
      </Card>

      {selected ? (
        <SelectedLineCard
          key={selected.id}
          line={selected}
          busy={scan.isPending || setCounted.isPending}
          onIncrement={() => scan.mutate({ kind: "MANUAL", productId: selected.productId, quantity: "1" })}
          onDecrement={() =>
            scan.mutate({ kind: "CORRECTION", productId: selected.productId, quantity: "-1" })
          }
          onSet={(quantity) => setCounted.mutate({ lineId: selected.id, countedQuantity: quantity })}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t("market.counts.noSelection")}</p>
      )}

      <LineList count={count} selectedProductId={selectedProductId} onSelect={setSelectedProductId} />

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" size="lg" onClick={() => setCancelling(true)}>
          <X aria-hidden="true" />
          {t("market.counts.cancel")}
        </Button>
        <Button size="lg" onClick={() => setSubmitting(true)}>
          <Send aria-hidden="true" />
          {t("market.counts.submit")}
        </Button>
      </div>

      <SubmitDialog open={submitting} onOpenChange={setSubmitting} count={count} />
      <Dialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("market.counts.cancelTitle")}
        description={t("market.counts.cancelDescription")}
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelling(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              {t("market.counts.cancel")}
            </Button>
          </>
        }
      />
    </>
  );
}

function SelectedLineCard({
  line,
  busy,
  onIncrement,
  onDecrement,
  onSet,
}: {
  line: StockCountLine;
  busy: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  onSet: (quantity: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const setId = useId();
  useEffect(() => setText(""), [line.countedQuantity]);

  return (
    <Card
      className="grid gap-4 p-4 sm:p-5"
      aria-label={t("market.counts.selectedCard")}
      data-testid="count-card"
    >
      <div className="grid gap-1">
        <h2 className="text-xl font-semibold">{line.productName}</h2>
        <p className="font-mono text-sm text-muted-foreground">
          {line.productCode}
          {line.barcode ? ` · ${line.barcode}` : ""}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Button
          variant="outline"
          className="size-16 [&_svg]:size-8"
          aria-label={t("market.counts.decrement", { product: line.productName })}
          disabled={busy || !canDecrement(line)}
          onClick={onDecrement}
        >
          <Minus aria-hidden="true" />
        </Button>
        <div className="grid min-w-32 justify-items-center">
          <span className="text-xs text-muted-foreground">{t("market.counts.counted")}</span>
          <span className="text-4xl font-semibold tabular" data-testid="counted-quantity">
            {line.countedQuantity === null ? "—" : formatStockQuantity(line.countedQuantity)}
          </span>
        </div>
        <Button
          className="size-16 [&_svg]:size-8"
          aria-label={t("market.counts.increment", { product: line.productName })}
          disabled={busy}
          onClick={onIncrement}
        >
          <Plus aria-hidden="true" />
        </Button>
      </div>
      <form
        className="grid gap-1.5"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const target = countedTarget(line.countedQuantity, text);
          if (!target.ok) {
            setProblem(t(`market.counts.setProblem.${target.reason}`));
            return;
          }
          setProblem(null);
          onSet(target.quantity);
        }}
      >
        <label htmlFor={setId} className="text-sm font-medium">
          {t("market.counts.setCounted")}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id={setId}
            inputMode="decimal"
            className="h-12 w-40 text-right text-lg tabular"
            aria-invalid={problem !== null}
            aria-describedby={problem ? `${setId}-error ${setId}-hint` : `${setId}-hint`}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <Button
            type="submit"
            size="lg"
            variant="outline"
            className="h-12"
            disabled={busy || text.trim() === ""}
          >
            <Check aria-hidden="true" />
            {t("market.counts.apply")}
          </Button>
        </div>
        {problem ? (
          <p id={`${setId}-error`} role="alert" className="text-xs font-medium text-destructive">
            {problem}
          </p>
        ) : null}
        <p id={`${setId}-hint`} className="text-xs text-muted-foreground">
          {t("market.counts.setCountedHint")}
        </p>
      </form>
    </Card>
  );
}

function LineList({
  count,
  selectedProductId,
  onSelect,
}: {
  count: StockCount;
  selectedProductId: string | null;
  onSelect: (productId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<CountLineFilter>("uncounted");
  const [search, setSearch] = useState("");
  const visible = filterCountLines(count.lines, filter, search);
  const selectable = count.status === "COUNTING";

  return (
    <Section title={t("market.counts.linesTitle")}>
      <Tabs
        value={filter}
        onValueChange={(next) => setFilter(next as CountLineFilter)}
        className="grid gap-3"
      >
        <div className="flex flex-wrap items-end gap-3">
          <TabsList aria-label={t("market.counts.linesFilter")}>
            {(["uncounted", "counted", "all"] as const).map((value) => (
              <TabsTrigger key={value} value={value}>
                {t(`market.counts.filter.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <FormField label={t("common.search")} className="w-60">
            {(control) => (
              <Input
                {...control}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("market.counts.searchPlaceholder")}
              />
            )}
          </FormField>
        </div>
        <TabsContent value={filter} className="grid gap-2 pt-0">
          {visible.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("market.counts.noLines")}</p>
          ) : (
            <ul className="grid gap-1.5" aria-label={t("market.counts.linesTitle")}>
              {visible.slice(0, LIST_LIMIT).map((line) => (
                <li key={line.id}>
                  <button
                    type="button"
                    disabled={!selectable}
                    aria-pressed={selectable ? line.productId === selectedProductId : undefined}
                    onClick={() => onSelect(line.productId)}
                    className={cn(
                      "flex min-h-12 w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm",
                      selectable && "hover:bg-muted",
                      line.productId === selectedProductId && "border-primary bg-primary/5",
                    )}
                  >
                    <span className="grid gap-0.5">
                      <span className="font-medium">{line.productName}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {line.productCode}
                        {line.barcode ? ` · ${line.barcode}` : ""}
                      </span>
                    </span>
                    <span className="text-base font-semibold tabular">
                      {line.countedQuantity === null ? (
                        <span className="text-sm font-normal text-muted-foreground">
                          {t("market.counts.notCounted")}
                        </span>
                      ) : (
                        formatStockQuantity(line.countedQuantity)
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {visible.length > LIST_LIMIT ? (
            <p className="text-sm text-muted-foreground">
              {t("market.counts.listLimited", { shown: LIST_LIMIT, total: visible.length })}
            </p>
          ) : null}
        </TabsContent>
      </Tabs>
    </Section>
  );
}

function SubmitDialog({
  open,
  onOpenChange,
  count,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: StockCount;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const [uncountedAsZero, setUncountedAsZero] = useState(false);
  const uncounted = count.lines.length - countProgress(count.lines).counted;

  const submit = useMutation({
    mutationFn: async () => {
      // Okutmalar sürümü ilerletmiş olabilir: gönderim GÜNCEL sürümle yapılır.
      const fresh = (await api.GET("/market/counts/{id}", { params: { path: { id: count.id } } })).data!;
      return (
        await api.POST("/market/counts/{id}/submit", {
          params: { path: { id: count.id } },
          body: { version: fresh.version, uncountedAsZero },
        })
      ).data!;
    },
    onSuccess: async () => {
      onOpenChange(false);
      await refresh();
      toast({ tone: "success", title: t("market.counts.done.submit") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("market.counts.submitTitle")}
      description={t("market.counts.submitDescription")}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button loading={submit.isPending} onClick={() => submit.mutate()}>
            <Send aria-hidden="true" />
            {t("market.counts.submit")}
          </Button>
        </>
      }
    >
      {uncounted > 0 ? (
        <div className="grid gap-3">
          <Alert tone="warning">{t("market.counts.uncountedWarning", { count: uncounted })}</Alert>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-1"
              checked={uncountedAsZero}
              onChange={(event) => setUncountedAsZero(event.target.checked)}
            />
            <span className="grid gap-0.5">
              <span className="font-medium">{t("market.counts.uncountedAsZero")}</span>
              <span className="text-xs text-muted-foreground">{t("market.counts.uncountedAsZeroHint")}</span>
            </span>
          </label>
        </div>
      ) : (
        <p className="text-sm">{t("market.counts.allCounted")}</p>
      )}
    </Dialog>
  );
}

// ── Fark raporu ──────────────────────────────────────────────────────────────
function DifferenceReport({ count }: { count: StockCount }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const withStepUp = useStepUp();
  const permissions = useMarketPermissions();
  const actions = countActions(count);
  const summary = differenceSummary(count);
  const [onlyDifferences, setOnlyDifferences] = useState(true);
  const rows = onlyDifferences ? filterCountLines(count.lines, "difference") : count.lines;

  const act = useMutation({
    mutationFn: async (action: "approve" | "reopen" | "cancel") => {
      const params = { path: { id: count.id } };
      const fresh = (await api.GET("/market/counts/{id}", { params })).data!;
      const body = { version: fresh.version };
      if (action === "approve")
        return withStepUp(
          async () => (await api.POST("/market/counts/{id}/approve", { params, body })).data!,
        );
      if (action === "reopen") return (await api.POST("/market/counts/{id}/reopen", { params, body })).data!;
      return (await api.POST("/market/counts/{id}/cancel", { params, body })).data!;
    },
    onSuccess: async (saved, action) => {
      await refresh();
      const key =
        action === "approve"
          ? (saved as StockCount).approvalPending
            ? "approvalRequested"
            : "approve"
          : action;
      toast({ tone: "success", title: t(`market.counts.done.${key}`) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const columns = useMemo<DataTableColumn<StockCountLine>[]>(
    () => [
      {
        id: "product",
        header: t("market.fields.product"),
        hideable: false,
        exportValue: (row) => `${row.productCode} ${row.productName}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.productName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.productCode}</span>
          </span>
        ),
      },
      {
        id: "snapshot",
        header: t("market.counts.fields.snapshot"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => quantityExport(row.snapshotQuantity),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.snapshotQuantity)}</span>,
      },
      {
        id: "movements",
        header: t("market.counts.fields.movementsSinceSnapshot"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => quantityExport(row.movementsSinceSnapshot),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.movementsSinceSnapshot)}</span>,
      },
      {
        id: "expected",
        header: t("market.counts.fields.expected"),
        align: "right",
        exportValue: (row) => quantityExport(row.expectedQuantity),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.expectedQuantity)}</span>,
      },
      {
        id: "counted",
        header: t("market.counts.counted"),
        align: "right",
        exportValue: (row) => quantityExport(row.countedQuantity),
        cell: (row) =>
          row.countedQuantity === null ? (
            <span className="text-muted-foreground">{t("market.counts.notCounted")}</span>
          ) : (
            <span className="tabular">{formatStockQuantity(row.countedQuantity)}</span>
          ),
      },
      {
        id: "difference",
        header: t("market.counts.fields.difference"),
        align: "right",
        hideable: false,
        exportValue: (row) => quantityExport(row.differenceQuantity),
        cell: (row) => {
          const direction = differenceDirection(row.differenceQuantity);
          return (
            <span className="grid justify-items-end gap-1">
              <span className="tabular font-medium">{formatStockQuantity(row.differenceQuantity)}</span>
              {direction === "NONE" ? null : (
                <StatusBadge tone={direction === "SHORTAGE" ? "danger" : "info"}>
                  {t(`market.counts.direction.${direction}`)}
                </StatusBadge>
              )}
            </span>
          );
        },
      },
      {
        id: "value",
        header: t("market.counts.fields.differenceValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.differenceValue),
        cell: (row) => <MoneyText value={row.differenceValue} colorize />,
      },
    ],
    [t],
  );

  return (
    <>
      {count.status === "CANCELLED" ? <Alert tone="info">{t("market.counts.cancelledHint")}</Alert> : null}
      {count.approvalPending ? <Alert tone="info">{t("market.counts.approvalPendingHint")}</Alert> : null}

      <Section
        title={t("market.counts.reportTitle")}
        actions={
          <>
            {actions.reopen && permissions.canManageCounts ? (
              <Button
                variant="outline"
                loading={act.isPending && act.variables === "reopen"}
                onClick={() => act.mutate("reopen")}
              >
                <Undo2 aria-hidden="true" />
                {t("market.counts.reopen")}
              </Button>
            ) : null}
            {actions.cancel && permissions.canManageCounts ? (
              <Button
                variant="outline"
                loading={act.isPending && act.variables === "cancel"}
                onClick={() => act.mutate("cancel")}
              >
                <X aria-hidden="true" />
                {t("market.counts.cancel")}
              </Button>
            ) : null}
            {actions.approve && permissions.canApproveCounts ? (
              <Button
                loading={act.isPending && act.variables === "approve"}
                onClick={() => act.mutate("approve")}
              >
                <Check aria-hidden="true" />
                {t("market.counts.approve")}
              </Button>
            ) : null}
          </>
        }
      >
        <dl className="grid gap-3 sm:grid-cols-3" data-testid="count-summary">
          <Field label={t("market.counts.shortageTotal", { count: summary.shortageLines })}>
            <MoneyText value={summary.shortageValue} />
          </Field>
          <Field label={t("market.counts.surplusTotal", { count: summary.surplusLines })}>
            <MoneyText value={summary.surplusValue} />
          </Field>
          <Field label={t("market.counts.fields.netValue")}>
            <MoneyText value={summary.netValue} colorize />
          </Field>
          <Field label={t("market.counts.fields.snapshotAt")}>{formatDateTime(count.snapshotAt)}</Field>
          <Field label={t("market.counts.fields.submittedAt")}>{formatDateTime(count.submittedAt)}</Field>
          <Field label={t("market.fields.ledger")}>
            <LedgerStatus status={count.ledgerStatus} journalEntryId={count.journalEntryId} />
          </Field>
        </dl>
        {summary.consistent ? null : <Alert tone="warning">{t("market.counts.inconsistent")}</Alert>}
        {actions.approve && !permissions.canApproveCounts ? (
          <p className="text-sm text-muted-foreground">{t("market.counts.cannotApprove")}</p>
        ) : null}
        {actions.approve && permissions.canApproveCounts ? (
          <p className="text-sm text-muted-foreground">{t("market.counts.approveHint")}</p>
        ) : null}
      </Section>

      <DataTable
        caption={t("market.counts.reportCaption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        emptyState={<p className="p-4 text-sm text-muted-foreground">{t("market.counts.noDifferences")}</p>}
        filters={
          <label className="flex min-h-10 items-center gap-2 text-sm">
            <Checkbox
              checked={onlyDifferences}
              onChange={(event) => setOnlyDifferences(event.target.checked)}
            />
            {t("market.counts.onlyDifferences")}
          </label>
        }
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: `sayim-${count.countNo}`,
            sheetName: t("market.counts.reportCaption"),
            columns: visible,
            rows: count.lines,
          });
        }}
      />

      {count.approvalRequestId ? (
        <Section title={t("market.counts.approvalTitle")}>
          <ApprovalRequestPanel requestId={count.approvalRequestId} showSummary={false} />
        </Section>
      ) : null}
    </>
  );
}
